import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '../../store/AppStore';
import {
  setFeatureStatus,
  updateFeatureStatus,
  updateToInitialFeatureMA,
} from '../../store/states/procos';
import { EDataTableType, ERecordLength } from '../../data/Constants';
import { ProCosDataTable } from './ProcosDataTable';
import { GridRowSelectionModel } from '@mui/x-data-grid';

import type { IFeature } from '../../../types/ITypes';

/**
 * To loop through all features and options and update the feature active/inactive status
 * @param {IFeature[]} features  list of features
 * @param { number } page the current page
 * @param {number} limit limit
 * @param { boolean } featureStatus  key pair value of feature status active /inActive
 * @returns {IFeature[]} return the array of feature
 */

const getRows = (
  features: IFeature[] | null,
  page: number,
  limit: number,
  featureStatus: { [key: string]: boolean }
): IFeature[] => {
  const rows: IFeature[] = [];
  const featureData: IFeature[] = structuredClone(features) as IFeature[];
  if (featureData && featureData.length > 0) {
    let count = 1;
    featureData.forEach((row: IFeature) => {
      if (count > (page - 1) * limit && count <= page * limit) {
        row.isActive = featureStatus[row.id];
        rows.push(row);
      }
      count++;
    });
  }
  return rows;
};

/**
 * This component renders the Data Table which returns the MA for Features and Options
 * @returns {JSX.Element} a  Features DataTable.
 */
export const FeaturesMarketAssociation = () => {
  const [rows, setRows] = useState<IFeature[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  });
  const [limit, setLimit] = useState(ERecordLength.RL100);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  const dispatch = useDispatch<AppDispatch>();

  const dialog = useSelector((state: AppState) => state.dialog);
  const procos = useSelector((state: AppState) => state.procos);
  const featureDetails = procos.features.data;

  useEffect(() => {
    if (featureDetails) {
      setPageCount(Math.ceil(featureDetails.length / limit));
      setRows(getRows(featureDetails, page, limit, procos.features.status.updated));
    } else {
      setRows([]);
    }
  }, [featureDetails]);

  //remove the check box selection when close the dialog
  useEffect(() => {
    if (!dialog.show && selectedFeatures.ids.size > 0) {
      setSelectedFeatures({ type: 'include', ids: new Set() });
    }
  }, [dialog.show]);

  //Refresh the rows when click feature status change
  useEffect(() => {
    setRows(getRows(featureDetails, page, limit, procos.features.status.updated));
  }, [procos.features.status.updated]);

  //To Handle Records Change
  const handleRecordsChange = (limit: number) => {
    setLimit(limit);
    setPage(1);
    setPageCount(Math.ceil(featureDetails.length / limit));
    setRows(getRows(featureDetails, 1, limit, procos.features.status.updated));
  };

  //To handle Page change
  const handlePageChange = (page: number) => {
    setPage(page);
    setRows(getRows(featureDetails, page, limit, procos.features.status.updated));
  };

  //dispatch the feature status to state
  const handleClickActive = () => {
    const activeFeatures: string[] = [];
    const currentFeatureStatus = { ...procos.features.status.updated };
    selectedFeatures.ids.forEach((feature) => {
      currentFeatureStatus[feature] = !currentFeatureStatus[feature];
      if (currentFeatureStatus[feature]) {
        activeFeatures.push(feature);
      }
    });

    dispatch(setFeatureStatus(false));
    dispatch(updateFeatureStatus(currentFeatureStatus));
    dispatch(updateToInitialFeatureMA(activeFeatures));

    setRows(getRows(featureDetails, page, limit, currentFeatureStatus));
    setSelectedFeatures({ type: 'include', ids: new Set() });
  };

  //set selected rows to the local state
  const handleCheckRow = (e) => {
    if (e?.ids?.size > 0) {
      dispatch(setFeatureStatus(true)); //enable to active/inactive button
    } else {
      dispatch(setFeatureStatus(false)); //disable the active/inactive button
    }
    setSelectedFeatures({ type: 'include', ids: e.ids });
  };

  return (
    <ProCosDataTable
      className="features-table"
      rows={rows}
      tableType={EDataTableType.Features}
      pageCount={pageCount}
      page={page}
      limit={limit}
      handlePageChange={handlePageChange}
      handleRecordsChange={handleRecordsChange}
      handleClickActive={handleClickActive}
      handleCheckRow={handleCheckRow}
      rowSelectionModel={selectedFeatures}
    />
  );
};
